import { Box, CSS } from '@project44/manifest-ds';
import {
  DateFormat,
  formatDateTime,
  getDateDiffDays,
  hasDateTimeWindow,
} from '@project44/ui-utils';

import StackedFormat from './StackedFormat';

import { DateTimeWrapper, DiffDays, Timezone } from '../styles';

export enum DateTimeDisplayFormat {
  SHORT = 'short',
  LONG = 'long',
  STACKED = 'stacked',
}

interface DateTimeWindowProps {
  css?: CSS;
  dateDiff?: React.ReactNode | React.ReactNode[];
  endDateTime?: Date | string;
  format?: DateTimeDisplayFormat;
  isPrediction?: boolean;
  isStale?: boolean;
  label?: string;
  startDateTime?: Date | string;
  startIcon?: React.ReactNode;
  timeZone?: string;
}

const Middot = () => (
  <Box as="span" className="middot">
    &middot;
  </Box>
);

/**
 * Displays formatted date with optional time window. To display without time window either omit `endDateTime` or
 * pass the same date to `startDateTime` and `endDateTime`.
 * Return formats:
 * - no window: `Jun 04 · 23:48`
 * - with window: `Jun 04 · 23:48-23:55`
 * - with window > 0 days: `Jun 04 · 23:48-01:55+1`
 * - with no `startDateTime`: `-`
 */
export const DateTime = ({
  css,
  dateDiff,
  endDateTime = '',
  format = DateTimeDisplayFormat.SHORT,
  isPrediction,
  isStale,
  label = '',
  startDateTime = '',
  startIcon,
  timeZone,
}: DateTimeWindowProps) => {
  const hasTimeWindow = hasDateTimeWindow({
    startDateTime,
    endDateTime,
  });

  const diffDays = hasTimeWindow
    ? getDateDiffDays({ startDateTime, endDateTime })
    : 0;

  // `formatDateTime` already has a default timeZone so only add it here if we have a value (override)
  const dateTimeTimezone = timeZone ? { timeZone } : {};

  const monthDay = formatDateTime({
    dateTime: startDateTime,
    dateFormat: DateFormat.shortMonthDay,
    ...dateTimeTimezone,
  });

  const monthDayYear = formatDateTime({
    dateTime: startDateTime,
    dateFormat: DateFormat.monthDayYear,
    ...dateTimeTimezone,
  });

  const startHourMinute = formatDateTime({
    dateTime: startDateTime,
    dateFormat: DateFormat.hourMinute,
    ...dateTimeTimezone,
  });

  const endHourMinute = formatDateTime({
    dateTime: endDateTime,
    dateFormat: DateFormat.hourMinute,
    ...dateTimeTimezone,
  });

  const timezoneAbbreviation =
    format !== DateTimeDisplayFormat.SHORT
      ? formatDateTime({
          dateTime: new Date(),
          dateFormat: DateFormat.timezone,
          ...dateTimeTimezone,
        })
      : '';

  if (!startDateTime) {
    if (format !== DateTimeDisplayFormat.STACKED) {
      return <span data-testid="date-time-no-date">-</span>;
    }
  }

  if (format === DateTimeDisplayFormat.STACKED) {
    return (
      <StackedFormat
        dateDiff={dateDiff}
        diffDays={diffDays}
        endHourMinute={endHourMinute}
        hasTimeWindow={hasTimeWindow}
        isPrediction={isPrediction}
        isStale={isStale}
        label={label}
        monthDayYear={monthDayYear}
        startHourMinute={startHourMinute}
        timezoneAbbreviation={timezoneAbbreviation}
      />
    );
  }

  return hasTimeWindow ? (
    <DateTimeWrapper as="span" data-testid="with-window" css={css}>
      {startIcon}
      {format === DateTimeDisplayFormat.SHORT
        ? monthDay && (
            <span data-testid="month-day">
              {monthDay} <Middot />{' '}
            </span>
          )
        : monthDayYear && (
            <span data-testid="month-day-year">
              {monthDayYear} <Middot />{' '}
            </span>
          )}
      {startHourMinute}-{endHourMinute}
      {diffDays > 0 && (
        <DiffDays data-testid="diff-days">{`+${diffDays}`}</DiffDays>
      )}
      {timezoneAbbreviation && (
        <Timezone data-testid="timezone" addPadding={diffDays > 0}>
          {' '}
          {timezoneAbbreviation}
        </Timezone>
      )}
    </DateTimeWrapper>
  ) : (
    <DateTimeWrapper as="span" data-testid="no-window" css={css}>
      {startIcon}
      {format === DateTimeDisplayFormat.SHORT
        ? monthDay && (
            <span data-testid="month-day">
              {monthDay} <Middot />{' '}
            </span>
          )
        : monthDayYear && (
            <span data-testid="month-day-year">
              {monthDayYear} <Middot />{' '}
            </span>
          )}
      {startHourMinute} {timezoneAbbreviation}
    </DateTimeWrapper>
  );
};

export default DateTime;
